import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import {
  LEGAL_DOCUMENT_TITLE,
  PRIVACY_INTRO,
  PRIVACY_SECTIONS,
  TERMS_INTRO,
  TERMS_SECTIONS,
} from '../lib/legalDocuments';

function SectionBlock({ section }) {
  return (
    <section className="space-y-3">
      <h3 className="text-base font-bold text-gray-900">{section.title}</h3>
      {(section.paragraphs || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-gray-700">
          {paragraph}
        </p>
      ))}
      {Array.isArray(section.bullets) && section.bullets.length > 0 ? (
        <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700">
          {section.bullets.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      ) : null}
      {(section.afterBullets || []).map((paragraph) => (
        <p key={paragraph} className="text-sm leading-relaxed text-gray-700">
          {paragraph}
        </p>
      ))}
    </section>
  );
}

export default function LegalDocumentsModal({
  open,
  initialSection = 'terms',
  logoSrc = '/assets/echojham.logo2.png',
  initialAcceptedLegal = false,
  initialMarketingOptIn = false,
  onDecline,
  onAccept,
}) {
  const scrollRef = useRef(null);
  const termsRef = useRef(null);
  const privacyRef = useRef(null);
  const [modalAcceptedLegal, setModalAcceptedLegal] = useState(initialAcceptedLegal);
  const [modalMarketingOptIn, setModalMarketingOptIn] = useState(initialMarketingOptIn);

  useEffect(() => {
    if (!open) return undefined;

    setModalAcceptedLegal(initialAcceptedLegal);
    setModalMarketingOptIn(initialMarketingOptIn);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const frame = window.requestAnimationFrame(() => {
      if (initialSection === 'privacy' && privacyRef.current) {
        privacyRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, initialSection, initialAcceptedLegal, initialMarketingOptIn]);

  const scrollToSection = (section) => {
    const target = section === 'privacy' ? privacyRef.current : termsRef.current;
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 px-4 py-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Terms of Use and Privacy Policy"
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-[#f3f2f1] shadow-2xl"
      >
        <div className="relative flex items-center justify-center border-b border-white/10 bg-black px-5 py-3">
          <img
            src={logoSrc}
            alt="Echo Jham"
            className="h-10 w-auto max-w-[180px] object-contain md:h-12"
          />
          <button
            type="button"
            onClick={onDecline}
            aria-label="Close legal documents"
            className="absolute right-5 top-1/2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-white/20 text-white/80 hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-5 md:px-7">
          <h3 className="mb-4 text-center text-xl font-black uppercase tracking-tight text-[#8b0000]">
            {LEGAL_DOCUMENT_TITLE}
          </h3>

          <div className="space-y-6">
            <section ref={termsRef} className="space-y-4" id="legal-terms-section">
              <h3 className="text-lg font-black uppercase text-gray-900">1. Terms of Use</h3>
              <p className="text-sm leading-relaxed text-gray-700">{TERMS_INTRO}</p>
              {TERMS_SECTIONS.map((section) => (
                <SectionBlock key={section.id} section={section} />
              ))}
            </section>

            <section ref={privacyRef} className="space-y-4 border-t-2 border-[#8b0000]/35 pt-8 mt-2" id="legal-privacy-section">
              <h3 className="text-lg font-black uppercase text-gray-900">2. Privacy Policy</h3>
              <p className="text-sm leading-relaxed text-gray-700">{PRIVACY_INTRO}</p>
              {PRIVACY_SECTIONS.map((section) => (
                <SectionBlock key={section.id} section={section} />
              ))}
            </section>
          </div>
        </div>

        <div className="border-t border-gray-200 bg-[#f3f2f1] px-5 py-4 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={modalAcceptedLegal}
              onChange={(event) => setModalAcceptedLegal(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-[#8b0000] focus:ring-[#8b0000]"
            />
            <span className="text-[12px] leading-relaxed font-semibold text-gray-900">
              I acknowledge that I have read and agree to the{' '}
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  scrollToSection('terms');
                }}
                className="font-bold text-[#8b0000] hover:underline"
              >
                Terms of Use
              </button>{' '}
              and have read and understood the{' '}
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  scrollToSection('privacy');
                }}
                className="font-bold text-[#8b0000] hover:underline"
              >
                Privacy Policy
              </button>
              .
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={modalMarketingOptIn}
              onChange={(event) => setModalMarketingOptIn(event.target.checked)}
              className="mt-1 h-4 w-4 shrink-0 rounded border-gray-300 text-[#8b0000] focus:ring-[#8b0000]"
            />
            <span className="text-[12px] leading-relaxed font-semibold text-gray-900">
              I would like to receive news, event updates, discounts, and special offers from Echo Jham.
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3 pt-1">
            <button
              type="button"
              onClick={onDecline}
              className="w-full rounded-xl bg-gray-300 px-6 py-3.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-gray-400"
            >
              Decline
            </button>
            <button
              type="button"
              disabled={!modalAcceptedLegal}
              onClick={() => onAccept({
                acceptedLegal: true,
                marketingOptIn: modalMarketingOptIn,
              })}
              className="w-full rounded-xl bg-[#8b0000] px-6 py-3.5 text-xs font-black uppercase tracking-widest text-white transition hover:bg-[#6f0000] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
